import { useInkind } from '@gik/api/inkinds/inkind';
import { useIsMounted } from '@gik/core/hooks/hooks/useIsMounted';
import routes from '@gik/core/routes';
import { useInkindCan } from '@gik/core/store/permissions';
import bemBlock from '@gik/core/utils/bemBlock';
import { navigateTo } from '@gik/core/utils/navigateTo';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { getPageSettingsFeatureHref } from '@gik/inkind-page/components/InkindPageSettingsModal/utils/PageSettingsScrollNavBar';
import { Heading, Layout, UI } from '@gik/ui';
import { Button } from '@gik/ui-legacy/Button';
import { SvgIcon } from '@gik/ui-legacy/SvgIcon/SvgIcon';
import { IconButtonCircle } from '@gik/ui/componentMap';
import NewspaperIcon from '@heroicons/react/outline/NewspaperIcon';
import { useTranslation } from 'react-i18next';
import { RelatedArticles } from '.';
import { SidebarSection } from '../SidebarSection';
import { translationKeys } from './i18n/en';

interface IRelatedArticlesSectionProps {
  inkindRouteId: string;
  showDisable?: boolean;
  enabled: boolean;
}

function RelatedArticlesSectionComp({ inkindRouteId, enabled }: IRelatedArticlesSectionProps) {
  const { t } = useTranslation();
  const bem = bemBlock('related-articles-section');
  const { data: inkindPage } = useInkind(inkindRouteId);

  const isMounted = useIsMounted();
  const canEdit = useInkindCan('manage', inkindRouteId, inkindPage?.groupId) && isMounted();

  if (!enabled && !canEdit) return null;

  // TODO: extract append to DisableSidebarFeatureButton
  return (
    <SidebarSection
      title={
        <div>
          <Layout horizontal gap={2} className="items-center">
            <Heading as="span" level={2}>
              {t(translationKeys.title).toString()}
            </Heading>

            {canEdit && (
              <IconButtonCircle
                onClick={() => navigateTo(getPageSettingsFeatureHref(inkindRouteId, 'articles'))}
                size={UI.Size.LG}
                color={UI.Color.White}
                elevation={UI.Elevation.SM}
                iconColor={UI.Color.Secondary}
                iconSize={'18px'}
                name="settings"
              />
            )}
          </Layout>
        </div>
      }
      className={bem()}
    >
      {!enabled && canEdit && (
        <div className={'bg-neutral-50 p-4 px-6 rounded-lg text-center text-neutral-700 leading-gik'}>
          Turn on to display helpful articles for visitors to learn more about the situation
        </div>
      )}
      {enabled && (
        <>
          <RelatedArticles inkindRouteId={inkindRouteId} />
          <Button
            type={'a'}
            href={routes.blog}
            block
            prepend={<SvgIcon size="lg" Icon={NewspaperIcon} />}
            className={bem('see-all-button')}
          >
            {t(translationKeys.seeAllArticles).toString()}
          </Button>
        </>
      )}
    </SidebarSection>
  );
}

export const RelatedArticlesSection = withComponentErrorBoundary(RelatedArticlesSectionComp);
